import React from 'react';
import { token } from '@atlaskit/tokens';
import { type MessageDescriptor, useIntl } from '@atlassian/jira-intl';
import { IllustrationBlobBackground } from '../../../../common/ui/illustration/illustration-blob-background';
import { IllustrationWrapper } from '../../../../common/ui/illustration/illustration-wrapper';
import { IssueKeyText } from '../../../../common/ui/illustration/issue-key-text';
import { ProjectNameText } from '../../../../common/ui/illustration/project-name-text';
import { ProjectStyleText } from '../../../../common/ui/illustration/project-style-text';
import {
	COL_1_X_POSITION,
	COL_2_X_POSITION,
	COL_3_X_POSITION,
	ROW_1_Y_POSITION,
	ROW_2_Y_POSITION,
	ROW_3_Y_POSITION,
	ROW_4_Y_POSITION,
	ISSUE_KEY_TEXT_WIDTH,
} from './constants';
import messages from './messages';

export type Props = {
	isTmp: boolean;
	projectName: string;
	projectKey: string;
	teamManagedIllustrationFooter: MessageDescriptor;
	companyManagedIllustrationFooter: MessageDescriptor;
};

export const IllustrationBoardView = ({
	projectName,
	projectKey,
	isTmp,
	teamManagedIllustrationFooter,
	companyManagedIllustrationFooter,
}: Props) => {
	const { formatMessage } = useIntl();

	return (
		<IllustrationWrapper data-testId="project-create-importer-upload-flow.project-create-screen.illustration.jsw.wrapper">
			<IllustrationBlobBackground>
				<svg
					width="664"
					height="429"
					viewBox="0 0 664 429"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					role="img"
					x="12"
					y="50"
					aria-label={formatMessage(messages.illustrationLabel)}
				>
					<g filter="url(#filter0_dd_3207_1683)">
						<rect
							x="12"
							y="4.12842"
							width="640"
							height="404"
							rx="8"
							fill={token('elevation.surface', 'white')}
						/>
						<rect
							x="212.448"
							y="48"
							width="36.3748"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<rect
							x="258.743"
							y="48"
							width="36.3748"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<rect
							x="312.479"
							y="48"
							width="47.9487"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<rect
							x="370.348"
							y="48"
							width="38.0282"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<rect
							x="212"
							y="20"
							width="119.872"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<rect
							width="133.333"
							height="316"
							transform="translate(212 77.0537)"
							fill={token('elevation.surface.sunken', '#F4F5F7')}
						/>
						<rect
							x="224"
							y="87.0537"
							width="60.3492"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<g filter="url(#filter1_dd_3207_1683)">
							<rect
								x="224"
								y="107.054"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="232"
								y="115.054"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="232"
								y="124.054"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_1_X_POSITION}
								y={ROW_1_Y_POSITION}
								projectKey={projectKey}
								issueNumber={1}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<g filter="url(#filter2_dd_3207_1683)">
							<rect
								x="224"
								y="165.321"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="232"
								y="173.321"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="232"
								y="182.321"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_1_X_POSITION}
								y={ROW_2_Y_POSITION}
								projectKey={projectKey}
								issueNumber={2}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<g filter="url(#filter3_dd_3207_1683)">
							<rect
								x="224"
								y="223.589"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="232"
								y="231.589"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="232"
								y="240.589"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_1_X_POSITION}
								y={ROW_3_Y_POSITION}
								projectKey={projectKey}
								issueNumber={3}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<g filter="url(#filter4_dd_3207_1683)">
							<rect
								x="224"
								y="281.856"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="232"
								y="289.856"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="232"
								y="298.856"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_1_X_POSITION}
								y={ROW_4_Y_POSITION}
								projectKey={projectKey}
								issueNumber={4}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<rect
							width="133.333"
							height="316"
							transform="translate(357.333 77.0537)"
							fill={token('elevation.surface.sunken', '#F4F5F7')}
						/>
						<rect
							x="369.333"
							y="87.0537"
							width="60.3492"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<g filter="url(#filter5_dd_3207_1683)">
							<rect
								x="369.333"
								y="107.054"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="377.333"
								y="115.054"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="377.333"
								y="124.054"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_2_X_POSITION}
								y={ROW_1_Y_POSITION}
								projectKey={projectKey}
								issueNumber={5}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<g filter="url(#filter6_dd_3207_1683)">
							<rect
								x="369.333"
								y="165.321"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="377.333"
								y="173.321"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="377.333"
								y="182.321"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_2_X_POSITION}
								y={ROW_2_Y_POSITION}
								projectKey={projectKey}
								issueNumber={6}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<rect
							width="133.333"
							height="316"
							transform="translate(502.667 77.0537)"
							fill={token('elevation.surface.sunken', '#F4F5F7')}
						/>
						<rect
							x="514.667"
							y="87.0537"
							width="60.3492"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<g filter="url(#filter7_dd_3207_1683)">
							<rect
								x="514.667"
								y="107.054"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="522.667"
								y="115.054"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="522.667"
								y="124.054"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_3_X_POSITION}
								y={ROW_1_Y_POSITION}
								projectKey={projectKey}
								issueNumber={7}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<g filter="url(#filter8_dd_3207_1683)">
							<rect
								x="514.667"
								y="165.321"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="522.667"
								y="173.321"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="522.667"
								y="182.321"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_3_X_POSITION}
								y={ROW_2_Y_POSITION}
								projectKey={projectKey}
								issueNumber={8}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<g filter="url(#filter9_dd_3207_1683)">
							<rect
								x="514.667"
								y="223.589"
								width="105"
								height="50.2676"
								rx="2.63378"
								fill={token('elevation.surface.raised', 'white')}
							/>
							<rect
								x="522.667"
								y="231.589"
								width="87"
								height="5"
								rx="2.5"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<rect
								x="522.667"
								y="240.589"
								width="61.8938"
								height="5.26756"
								rx="2.63378"
								fill={token('color.background.accent.gray.subtler', '#EBECF0')}
							/>
							<IssueKeyText
								x={COL_3_X_POSITION}
								y={ROW_3_Y_POSITION}
								projectKey={projectKey}
								issueNumber={9}
								width={ISSUE_KEY_TEXT_WIDTH}
							/>
						</g>
						<path
							d="M12 11.4403C12 7.33113 15.3311 4 19.4403 4H196V408H19.4403C15.3311 408 12 404.669 12 400.56V11.4403Z"
							fill={token('elevation.surface.sunken', '#F4F5F7')}
						/>
						<rect
							x="28"
							y="20"
							width="19.8408"
							height="19.8408"
							rx="4.96021"
							fill={token('color.icon.accent.blue', '#2684FF')}
						/>
						<ProjectNameText x={57} y={19}>
							{projectName}
						</ProjectNameText>
						<rect
							x="28"
							y="55.8408"
							width="152"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<rect
							x="28"
							y="83.8408"
							width="152"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<rect
							x="28"
							y="111.841"
							width="152"
							height="12"
							rx="6"
							fill={token('color.background.accent.gray.subtler', '#DFE1E6')}
						/>
						<ProjectStyleText x={103} y={388}>
							{isTmp
								? formatMessage(teamManagedIllustrationFooter)
								: formatMessage(companyManagedIllustrationFooter)}
						</ProjectStyleText>
						<rect
							x="12.5"
							y="4.62842"
							width="639"
							height="403"
							rx="7.5"
							stroke={token('color.border', '#F4F5F7')}
						/>
					</g>
					<defs>
						<filter
							id="filter0_dd_3207_1683"
							x="0"
							y="0"
							width="664"
							height="428.128"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.5" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="8" />
							<feGaussianBlur stdDeviation="6" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.15 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter1_dd_3207_1683"
							x="217.416"
							y="104.42"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter2_dd_3207_1683"
							x="217.416"
							y="162.688"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter3_dd_3207_1683"
							x="217.416"
							y="220.955"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter4_dd_3207_1683"
							x="217.416"
							y="279.223"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter5_dd_3207_1683"
							x="362.749"
							y="104.42"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter6_dd_3207_1683"
							x="362.749"
							y="162.688"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter7_dd_3207_1683"
							x="508.082"
							y="104.42"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter8_dd_3207_1683"
							x="508.082"
							y="162.688"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
						<filter
							id="filter9_dd_3207_1683"
							x="508.082"
							y="220.955"
							width="118.169"
							height="63.4365"
							filterUnits="userSpaceOnUse"
							colorInterpolationFilters="sRGB"
						>
							<feFlood floodOpacity="0" result="BackgroundImageFix" />
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset />
							<feGaussianBlur stdDeviation="0.658444" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.31 0"
							/>
							<feBlend
								mode="normal"
								in2="BackgroundImageFix"
								result="effect1_dropShadow_3207_1683"
							/>
							<feColorMatrix
								in="SourceAlpha"
								type="matrix"
								values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
								result="hardAlpha"
							/>
							<feOffset dy="3.95067" />
							<feGaussianBlur stdDeviation="3.29222" />
							<feColorMatrix
								type="matrix"
								values="0 0 0 0 0.0352941 0 0 0 0 0.117647 0 0 0 0 0.258824 0 0 0 0.2 0"
							/>
							<feBlend
								mode="normal"
								in2="effect1_dropShadow_3207_1683"
								result="effect2_dropShadow_3207_1683"
							/>
							<feBlend
								mode="normal"
								in="SourceGraphic"
								in2="effect2_dropShadow_3207_1683"
								result="shape"
							/>
						</filter>
					</defs>
				</svg>
			</IllustrationBlobBackground>
		</IllustrationWrapper>
	);
};
